import { IAccessoryDraft, IAccessoryIdentifyResult } from '../interfaces/heavyDutyMachinery/IAccessoryAssist';
import { IServiceRequestClarification } from '../interfaces/heavyDutyMachinery/IServiceRequest';
import { MachineryAssistOutcome, assistFailureOf } from './MachineryAssistService';
import { apiService } from './ApiService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { HTTP_METHODS } from '../constants/httpMethods';

const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

/**
 * The publishing assistant for attachments.
 *
 * Shares MachineryAssistOutcome rather than declaring its own: the three ways
 * a call can fail -- switched off, spent for the day, or simply broken -- are
 * a property of the assistant rather than of what is being published, and two
 * copies would drift the first time a fourth appears.
 */

/** Which accessory type this is, from a sentence somebody typed or dictated. */
export const identifyAccessory = async (
  description: string,
  lang: string
): Promise<MachineryAssistOutcome<IAccessoryIdentifyResult>> => {
  const response = await apiService<IAccessoryIdentifyResult>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ACCESSORY_ASSIST_IDENTIFY,
    method: HTTP_METHODS.POST,
    body: { description, lang },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return { ok: true, value: { ...response.data, questions: response.data.questions ?? [] } };
  return assistFailureOf(response.status, response.error);
};

/**
 * The compatibility sheet for that attachment.
 *
 * No schema travels from here, unlike the machinery draft: an accessory's
 * fields are the same for every one of them, so the server already knows what
 * it is filling in. What it does NOT know is the machinery taxonomy the host
 * is reading in, which is why the language goes along -- the list of machines
 * it fits comes back as codes, but it was chosen from names.
 */
export const draftAccessory = async (
  description: string,
  lang: string,
  options: {
    accessoryTypeCode?: string;
    manufacturerBrand?: string;
    modelName?: string;
    answers?: IServiceRequestClarification[];
  } = {}
): Promise<MachineryAssistOutcome<IAccessoryDraft>> => {
  const response = await apiService<IAccessoryDraft>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ACCESSORY_ASSIST_DRAFT,
    method: HTTP_METHODS.POST,
    body: { description, lang, ...options },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) {
    return { ok: true, value: { ...response.data, compatibleCategories: response.data.compatibleCategories ?? [] } };
  }
  return assistFailureOf(response.status, response.error);
};
